import { Artifact, ArtifactFile, FormValues } from '../../types';

export function updateArtifactAt(
  values: FormValues,
  index: number,
  changes: Partial<Artifact>
): FormValues {
  return {
    ...values,
    artifacts: values.artifacts.map((artifact, i) =>
      i === index ? { ...artifact, ...changes } : artifact
    ),
  };
}

// Also repoints other artifacts' `dependencies` entries at a renamed artifact's new name.
export function renameArtifactAt(
  values: FormValues,
  index: number,
  changes: Partial<Artifact>
): FormValues {
  const updated = updateArtifactAt(values, index, changes);

  const previousName = values.artifacts[index].name;
  const newName = changes.name;
  if (typeof newName !== 'string' || newName === previousName) {
    return updated;
  }

  return {
    ...updated,
    artifacts: updated.artifacts.map((artifact, i) =>
      i === index
        ? artifact
        : {
            ...artifact,
            dependencies: artifact.dependencies.map((dependency) =>
              dependency === previousName ? newName : dependency
            ),
          }
    ),
  };
}

// Appends, so an Edge stack's entrypoint (its first file) stays put. An Edge
// artifact is one checkout, so a file from another source or ref replaces the
// lot; a reconciled artifact draws from several sources, so only this
// source's files at another ref go.
export function assignFileToArtifact(
  values: FormValues,
  targetIndex: number,
  file: ArtifactFile
): FormValues {
  const existing = values.artifacts[targetIndex].files;

  if (existing.some((f) => isSameFile(f, file))) {
    return values;
  }

  return updateArtifactAt(values, targetIndex, {
    files: [...keptFiles(values, targetIndex, file), file],
    // The namespace survives: it is where the release goes, not something
    // about the chart, so choosing different files does not unsay it. The
    // values files do not, being the old chart's.
    ...(values.engine === 'reconcile'
      ? {
          helm: {
            enabled: false,
            valuesFiles: [],
            namespace: values.artifacts[targetIndex].helm.namespace,
          },
        }
      : {}),
  });
}

/** The files dropping `file` on the artifact would remove. */
export function filesEvictedByDrop(
  values: FormValues,
  targetIndex: number,
  file: ArtifactFile
): Array<ArtifactFile> {
  const { files } = values.artifacts[targetIndex];
  if (files.some((f) => isSameFile(f, file))) {
    return [];
  }

  const kept = keptFiles(values, targetIndex, file);
  return files.filter((f) => !kept.includes(f));
}

function keptFiles(
  values: FormValues,
  targetIndex: number,
  file: ArtifactFile
) {
  return values.artifacts[targetIndex].files.filter((f) =>
    values.engine === 'reconcile'
      ? f.sourceId !== file.sourceId || f.ref === file.ref
      : f.sourceId === file.sourceId && f.ref === file.ref
  );
}

function isSameFile(a: ArtifactFile, b: ArtifactFile) {
  return a.sourceId === b.sourceId && a.path === b.path && a.ref === b.ref;
}
